import React, { forwardRef } from "react";
import { useToggle } from "../../../hooks/common";

export type InputProps<X = HTMLInputElement> = {
  label?: string;
  helperText?: string;
  error?: boolean;
  containerClass?: string;
  isOtpVerification?: boolean;
  onVerify?: () => void;
} & React.InputHTMLAttributes<X>;

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      helperText,
      error,
      isOtpVerification,
      onVerify,
      containerClass,
      ...props
    },
    ref
  ) => {
    return (
      <div className={containerClass}>
        {!!label && (
          <label
            {...(props.id && { htmlFor: props.id })}
            className="mb-2 font-semibold"
          >
            {label}
            {props.required && <span className="text-red-500"> *</span>}
          </label>
        )}
        <div
          className={`border border-gray-400 flex items-center w-full rounded-lg bg-white ${
            error ? "border-red-500" : ""
          }`}
        >
<div className="relative flex-grow">
  <div className="ios-date-wrapper">
    <input
      {...props}
      ref={ref}
      className={`${
        props.className
      } py-3 px-4 placeholder:px-0 block disabled:bg-gray-200 w-full h-12 rounded-lg ${
        props.type === 'date' ? 'ios-date-input' : ''
      }`}
    />
  </div>
</div>
          {!!isOtpVerification && (
            <p className="cursor-pointer font-bold mr-4" onClick={onVerify}>
              verify
            </p>
          )}
        </div>
        {!!helperText && (
          <p className={error ? "text-red-500" : ""}>{helperText}</p>
        )}
      </div>
    );
  }
);

export default Input;

export const PasswordInput = forwardRef<HTMLInputElement, InputProps>(
  ({ label, helperText, error, containerClass, ...props }, ref) => {
    const [show, toggleShow] = useToggle();
    return (
      <div className={containerClass}>
        {!!label && (
          <label
            {...(props.id && { htmlFor: props.id })}
            className="mb-2 font-semibold"
          >
            {label}
            {props.required && <span className="text-red-500"> *</span>}
            {/* {props.required ? `${label} *` : label} */}
          </label>
        )}
        <div
          className={`border border-gray-400 flex items-center w-full rounded-lg bg-white ${
            error ? "border-red-500" : ""
          }`}
        >
          <input
            {...props}
            type={show ? "text" : "password"}
            ref={ref}
            className={`${props.className}  py-3 grow px-4 placeholder:px-0  block disabled:bg-gray-200`}
          />
          <i
            className={`fa my-0 mx-4 ${show ? "fa-eye-slash" : "fa-eye"}`}
            onClick={toggleShow}
          ></i>
        </div>
        {!!helperText && (
          <p className={error ? "text-red-500" : ""}>{helperText}</p>
        )}
      </div>
    );
  }
);

export type TextAreaProps = {
  label?: string;
  helperText?: string;
  error?: boolean;
  containerClass?: string;
  onVerify?: () => void;
} & React.TextareaHTMLAttributes<HTMLTextAreaElement>;

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ label, helperText, error, onVerify, containerClass, onChange, ...props }, ref) => {
    const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const target = e.target;
      target.style.height = "auto";
      target.style.height = `${target.scrollHeight}px`;
      if (onChange) onChange(e);
    };

    return (
      <div className={containerClass}>
        {!!label && (
          <label
            {...(props.id && { htmlFor: props.id })}
            className="mb-2 font-semibold"
          >
            {label}
            {props.required && <span className="text-red-500"> *</span>}
          </label>
        )}
        <div
          className={`border border-gray-400 flex items-center w-full rounded-lg bg-white ${
            error ? "border-red-500" : ""
          }`}
        >
          <textarea
            {...props}
            ref={ref}
            onChange={handleInput}
            className={`py-3 px-4 w-full rounded-lg block placeholder:px-0 disabled:bg-gray-200 min-h-[90px] max-h-[240px] sm:min-h-[110px] sm:max-h-[300px] overflow-y-auto resize-y focus:outline-none ${
              props.className || ""
            }`}
          />
        </div>
        {!!helperText && (
          <p className={error ? "text-red-500" : ""}>{helperText}</p>
        )}
      </div>
    );
  }
);
